import React, { useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

export interface SelectOption {
  value: string | number;
  label: string;
}

type SelectSize = 'sm' | 'md' | 'lg';

interface SimpleSelectProps {
  name: string;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  icon?: React.ReactNode;
  defaultValue?: SelectOption | null;
  errorMessage?: string;
  className?: string;
  size?: SelectSize;
}

const SimpleSelect: React.FC<SimpleSelectProps> = ({
  name,
  options,
  label,
  placeholder = 'Select...',
  isRequired = false,
  icon,
  defaultValue = null,
  errorMessage,
  className = '',
  size = 'md',
}) => {
  const { register, setValue, watch } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(defaultValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentValue = watch(name);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set default value when component mounts or defaultValue changes
  useEffect(() => {
    if (defaultValue) {
      // We need to avoid calling setValue if the value is already set
      // This is crucial to prevent infinite loops
      const currentFormValue = watch(name);
      if (currentFormValue !== defaultValue.value) {
        setValue(name, defaultValue.value);
      }
      setSelectedOption(defaultValue);
    }
  }, [defaultValue, name, setValue, watch]);

  // Update selectedOption when value changes externally
  // Use a ref to track if we should update to prevent unnecessary updates
  const previousValueRef = useRef(currentValue);
  useEffect(() => {
    // Only update if the value has actually changed
    if (currentValue !== previousValueRef.current && options.length > 0) {
      previousValueRef.current = currentValue;
      const option = options.find(opt => opt.value === currentValue);
      if (option && (!selectedOption || selectedOption.value !== option.value)) {
        setSelectedOption(option);
      }
    }
  }, [currentValue, options, selectedOption]);

  const handleSelect = (option: SelectOption) => {
    setValue(name, option.value, { shouldValidate: true });
    setSelectedOption(option);
    setIsOpen(false);
  };

  // Get appropriate styles based on size
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'py-1.5 h-8',
          text: 'text-xs',
          icon: 'text-xs',
          dropdownItem: 'py-1.5 text-xs'
        };
      case 'lg':
        return {
          container: 'py-3 h-12',
          text: 'text-base',
          icon: 'text-base',
          dropdownItem: 'py-2.5 text-base'
        };
      case 'md':
      default:
        return {
          container: 'py-2.5 h-10',
          text: 'text-sm',
          icon: 'text-sm',
          dropdownItem: 'py-2 text-sm'
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <div className={className}>
      {label && (
        <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        ref={dropdownRef}
        className={`relative ${errorMessage ? 'border-red-500' : ''}`}
      >
        {/* Hidden input for form registration */}
        <input
          type="hidden"
          {...register(name)}
          value={selectedOption?.value?.toString() || ''}
        />

        {/* Clickable select field */}
        <button
          type="button"
          className={`flex items-center w-full px-4 
bg-gray-50 dark:bg-gray-800 
rounded-lg cursor-pointer 
border border-gray-300 dark:border-gray-600 
${sizeStyles.container}`}
          onClick={() => setIsOpen(prev => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        >
          {icon && <span className={`mr-2 ${sizeStyles.icon}`}>{icon}</span>}

          <div className={`flex-grow ${sizeStyles.text}`}>
            {selectedOption ? (
              <span className="text-gray-900 dark:text-gray-100">
                {selectedOption.label}
              </span>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">
                {placeholder}
              </span>
            )}
          </div>

          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={`text-gray-400 ${sizeStyles.icon}`}
          />
        </button>

        {/* Dropdown options */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                type="button"
                key={option.value}
                className={`w-full text-left px-4 hover:bg-gray-100 ${sizeStyles.dropdownItem} ${selectedOption?.value === option.value ? 'bg-blue-50 text-blue-600' : ''
                  }`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </button>

            ))}
            {options.length === 0 && (
              <div className={`px-4 text-gray-500 ${sizeStyles.dropdownItem}`}>No options available</div>
            )}
          </div>
        )}
      </div>

      {errorMessage && (
        <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};

export default SimpleSelect;
